import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, Image, StyleSheet, Platform, ActivityIndicator, Linking } from 'react-native';
import FullscreenSafeModal from './FullscreenSafeModal';
import { Ionicons } from '@expo/vector-icons';
import { getFirestore, collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, doc, getDocs, getDoc, serverTimestamp, limit, arrayUnion } from 'firebase/firestore';
import { useNavigation } from '@react-navigation/native';
import { useUser } from './LoginPersistence';
import { useCommunityModal } from '../contexts/CommunityModalContext';
import { PersonAvatar, avatarImageSource } from './PersonAvatar';
import { displayNameFromUserLike, personInitials } from '../utils/personInitials';
import { useTourCommunityHub } from '../contexts/FeatureFlagsContext';
import { useTourChatInbox } from '../hooks/useTourChatInbox';
// import { CommunityMemberUpgradeForm } from './CommunityMemberUpgradeForm'; // Re-enable when gating messaging for non-members
import PostGridDisplay from './PostGridDisplay';
import { respondToTourInvite } from '../utils/tourInviteService';

const GEAR_SWAP_TYPE_COLORS = { SALE: '#0a59f0', TRADE: '#03bb8d', ISO: '#4e5067' };

/*
SUGGESTED FIRESTORE DATABASE STRUCTURE FOR MESSAGING:

/conversations/{conversationId}
  - participants: [userId1, userId2, ...] // Array of user IDs
  - type: 'direct' | 'group'
  - name: string (for group chats, null for direct messages)
  - lastMessage: {
      text: string,
      senderId: string,
      timestamp: timestamp,
      type: 'text' | 'image' | 'file'
    }
  - lastActivity: timestamp
  - createdAt: timestamp
  - createdBy: string (userId)
  - unreadCounts: {
      [userId]: number // Unread count per user
    }
  - isActive: boolean

/conversations/{conversationId}/messages/{messageId}
  - text: string
  - senderId: string
  - senderName: string
  - senderPhoto: string
  - timestamp: timestamp
  - type: 'text' | 'image' | 'file' | 'system'
  - readBy: { userId1: timestamp1, userId2: timestamp2, ... } // Object mapping user IDs to read timestamps
  - edited: boolean
  - editedAt: timestamp (optional)
  - replyTo: messageId (optional, for threaded replies)

/users/{userId} (extend existing user document)
  - lastSeen: timestamp
  - isOnline: boolean
  - displayName: string
  - photoURL: string
  - preferences: {
      notifications: boolean,
      readReceipts: boolean
    }

INDEXES NEEDED:
- conversations: participants array-contains, lastActivity desc
- messages: timestamp asc, senderId
- conversations: participants array-contains, type
*/

const MessagingComponent = ({ messagingSenderId = null }) => {
  const { user, setUser } = useUser();
  const navigation = useNavigation();
  const showTourCommunityHub = useTourCommunityHub();
  const { messagingTargetUserId: targetUserId, messagingExistingConversationId: existingConversationId, closeModal } = useCommunityModal();
  const tourChatThreads = useTourChatInbox(user?.uid, showTourCommunityHub);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showCommunityMemberSuccessModal, setShowCommunityMemberSuccessModal] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [searchUsers, setSearchUsers] = useState('');
  const [foundUsers, setFoundUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupChatName, setGroupChatName] = useState('');
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [targetUserData, setTargetUserData] = useState(null);
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  const [tourInviteRespondingId, setTourInviteRespondingId] = useState('');
  
  const db = getFirestore();
  const messagesScrollRef = useRef(null);
  
  // Determine the actual sender ID (user or organization)
  const actualSenderId = messagingSenderId || user?.uid;
  
  // Get sender data (user or organization)
  const [senderData, setSenderData] = useState(null);
  
  useEffect(() => {
    const loadSenderData = async () => {
      if (!actualSenderId) {
        setSenderData(null);
        return;
      }
      
      if (messagingSenderId) {
        // Load organization data
        try {
          const orgDoc = await getDoc(doc(db, 'organizations', messagingSenderId));
          if (orgDoc.exists()) {
            setSenderData({
              id: orgDoc.id,
              entityType: 'organization',
              ...orgDoc.data()
            });
          } else {
            setSenderData(null);
          }
        } catch (error) {
          console.error('Error loading organization data:', error);
          setSenderData(null);
        }
      } else {
        // Use user data
        setSenderData({
          id: user?.uid,
          entityType: 'user',
          username: user?.username,
          firstName: user?.firstName,
          lastName: user?.lastName,
          photoURL: user?.photoURL
        });
      }
    };
    
    loadSenderData();
  }, [actualSenderId, messagingSenderId, user?.uid, user?.username, user?.firstName, user?.photoURL, db]);

  // Load user's conversations with participant user data
  useEffect(() => {
    console.log('💬 MessagingComponent: Setting up conversations listener', {
      actualSenderId: actualSenderId,
      messagingSenderId: messagingSenderId,
      user: user?.uid,
      hasUser: !!user
    });
    
    if (!actualSenderId) {
      console.log('💬 MessagingComponent: No sender ID, skipping listener setup');
      setConversations([]);
      setIsLoadingConversations(false);
      return;
    }

    // Set loading to true when starting to fetch
    setIsLoadingConversations(true);

    // Try with orderBy, but handle potential index error
    let conversationsQuery;
    try {
      conversationsQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', actualSenderId),
        orderBy('lastActivity', 'desc')
      );
    } catch (error) {
      console.log('💬 Using fallback query without orderBy due to:', error);
      conversationsQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', actualSenderId)
      );
    }

    const unsubscribe = onSnapshot(conversationsQuery, async (snapshot) => {
      console.log('💬 MessagingComponent: Conversations snapshot received', {
        size: snapshot.size,
        empty: snapshot.empty,
        actualSenderId: actualSenderId
      });
      
      const conversationsList = [];
      
      // Process each conversation and fetch participant data
      for (const docSnap of snapshot.docs) {
        const conversationData = { id: docSnap.id, ...docSnap.data() };
        
        // Fetch user/organization data for all participants
        const participantUsers = [];
        for (const participantId of conversationData.participants) {
          try {
            // First try to fetch as a user
            const userDocRef = doc(db, 'users', participantId);
            const userDocSnap = await getDoc(userDocRef);
            
            if (userDocSnap.exists()) {
              participantUsers.push({
                id: participantId,
                entityType: 'user',
                ...userDocSnap.data()
              });
            } else {
              // If not found in users, try organizations collection
              const orgDocRef = doc(db, 'organizations', participantId);
              const orgDocSnap = await getDoc(orgDocRef);
              
              if (orgDocSnap.exists()) {
                participantUsers.push({
                  id: participantId,
                  entityType: 'organization',
                  ...orgDocSnap.data()
                });
              } else {
                // Fallback for entities that don't exist in the database
                participantUsers.push({
                  id: participantId,
                  entityType: 'user',
                  firstName: 'Unknown',
                  lastName: 'User',
                  username: `user_${participantId.substring(0, 8)}`,
                  photoURL: null
                });
              }
            }
          } catch (error) {
            console.error(`Error fetching participant data for ${participantId}:`, error);
            // Fallback user data
            participantUsers.push({
              id: participantId,
              entityType: 'user',
              firstName: 'Unknown',
              lastName: 'User', 
              username: `user_${participantId.substring(0, 8)}`,
              photoURL: null
            });
          }
        }
        
        // Add participant user objects to conversation
        conversationData.participantUsers = participantUsers;
        conversationsList.push(conversationData);
      }

      console.log('💬 MessagingComponent: Setting conversations', conversationsList.length);
      setConversations(conversationsList);
      setIsLoadingConversations(false);
    }, (error) => {
      console.error('💬 MessagingComponent: Error in conversations listener:', error);
      setConversations([]);
      setIsLoadingConversations(false);
    });

    return () => unsubscribe();
  }, [actualSenderId, db]);

  // Clear selected conversation when sender changes if it doesn't belong to the new sender
  useEffect(() => {
    if (selectedConversation && actualSenderId) {
      // Check if the selected conversation includes the current sender
      const belongsToSender = selectedConversation.participants?.includes(actualSenderId);
      if (!belongsToSender && !selectedConversation.composeMode) {
        // Clear the conversation if it doesn't belong to the new sender
        setSelectedConversation(null);
        setMessages([]);
      }
    }
  }, [actualSenderId]);

  // Fetch target user data when targetUserId changes
  useEffect(() => {
    const fetchTargetUserData = async () => {
      if (targetUserId) {
        try {
          // First try to fetch as a user
          const userDocRef = doc(db, 'users', targetUserId);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            setTargetUserData({
              id: targetUserId,
              entityType: 'user',
              ...userDocSnap.data()
            });
          } else {
            // If not found in users, try organizations collection
            const orgDocRef = doc(db, 'organizations', targetUserId);
            const orgDocSnap = await getDoc(orgDocRef);
            
            if (orgDocSnap.exists()) {
              setTargetUserData({
                id: targetUserId,
                entityType: 'organization',
                ...orgDocSnap.data()
              });
            } else {
              // Fallback for entities that don't exist in the database
              setTargetUserData({
                id: targetUserId,
                entityType: 'user',
                firstName: 'Unknown',
                lastName: 'User',
                username: `user_${targetUserId.substring(0, 8)}`,
                photoURL: null
              });
            }
          }
        } catch (error) {
          console.error(`Error fetching target user/organization data for ${targetUserId}:`, error);
          // Fallback user data
          setTargetUserData({
            id: targetUserId,
            entityType: 'user',
            firstName: 'Unknown',
            lastName: 'User',
            username: `user_${targetUserId.substring(0, 8)}`,
            photoURL: null
          });
        }
      } else {
        setTargetUserData(null);
      }
    };

    fetchTargetUserData();
  }, [targetUserId]);

  // Handle target user from external navigation
  useEffect(() => {
    if (targetUserId) {
      // Check if there's an existing conversation with the target user
      const existingConv = conversations.find(conv => 
        conv.type === 'direct' && 
        conv.participants.includes(targetUserId) &&
        conv.participants.length === 2
      );
      
      if (existingConv) {
        setSelectedConversation(existingConv);
      } else {
        // Don't create conversation yet - wait for first message
        // Just set up the compose interface for the target user
        setSelectedConversation({
          id: 'compose',
          type: 'direct',
          participants: [actualSenderId, targetUserId],
          composeMode: true,
          targetUserId: targetUserId
        });
      }
    } else if (existingConversationId && conversations.length > 0) {
      // Select the existing conversation
      const existingConv = conversations.find(conv => conv.id === existingConversationId);
      if (existingConv) {
        setSelectedConversation(existingConv);
      }
    }
  }, [targetUserId, existingConversationId, conversations]);

  // Load messages for selected conversation
  useEffect(() => {
    if (!selectedConversation || selectedConversation.composeMode) {
      // Clear messages for compose mode
      setMessages([]);
      return;
    }

    const messagesQuery = query(
      collection(db, 'conversations', selectedConversation.id, 'messages'),
      orderBy('timestamp', 'asc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(messagesQuery, async (snapshot) => {
      const messagesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesList);
      
      // Scroll to bottom when new messages arrive
      setTimeout(() => {
        if (messagesScrollRef.current) {
          messagesScrollRef.current.scrollToEnd({ animated: true });
        }
      }, 100);

      // Mark messages as read (pass messagesList directly to avoid state timing issues)
      await markMessagesAsRead(messagesList);
    });

    return () => unsubscribe();
  }, [selectedConversation]);

  // Search for users to start new conversation
  const handleUserSearch = async (searchTerm) => {
    setSearchUsers(searchTerm);
    if (searchTerm.trim().length < 2) {
      setFoundUsers([]);
      return;
    }

    try {
      // Fetch all users and filter on frontend (like community search)
      const usersQuery = query(
        collection(db, 'users'),
        limit(100) // Reasonable limit to avoid fetching too many users
      );

      const snapshot = await getDocs(usersQuery);
      const allUsers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Filter users based on search term
      const searchLower = searchTerm.toLowerCase();
      const filteredUsers = allUsers
        .filter(foundUser => {
          // Exclude current user
          if (foundUser.id === user.uid) return false;
          
          // Search across multiple fields
          const firstName = (foundUser.firstName || '').toLowerCase();
          const lastName = (foundUser.lastName || '').toLowerCase();
          const username = (foundUser.username || '').toLowerCase();
          const email = (foundUser.email || '').toLowerCase();
          
          return firstName.includes(searchLower) ||
                 lastName.includes(searchLower) ||
                 username.includes(searchLower) ||
                 email.includes(searchLower) ||
                 `${firstName} ${lastName}`.includes(searchLower);
        })
        .slice(0, 10); // Limit to 10 results

      setFoundUsers(filteredUsers);

    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  // Start new conversation (for manual user selection)
  const startConversation = async (targetUser) => {
    try {
      // Check if direct conversation already exists
      const existingConversationQuery = query(
        collection(db, 'conversations'),
        where('participants', '==', [actualSenderId, targetUser.id].sort()),
        where('type', '==', 'direct')
      );

      const existingSnapshot = await getDocs(existingConversationQuery);
      
      if (!existingSnapshot.empty) {
        // Conversation already exists, select it
        const existingConversationData = existingSnapshot.docs[0].data();
        const existingConversation = {
          id: existingSnapshot.docs[0].id,
          ...existingConversationData,
          participantUsers: await fetchParticipantUsers(existingConversationData.participants)
        };
        setSelectedConversation(existingConversation);
        setShowNewMessageModal(false);
        return;
      }

      // Set up compose mode for new conversation
      setSelectedConversation({
        id: 'compose',
        type: 'direct',
        participants: [actualSenderId, targetUser.id],
        composeMode: true,
        targetUserId: targetUser.id
      });

      setShowNewMessageModal(false);
      setSearchUsers('');
      setFoundUsers([]);
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  // Create group chat
  const createGroupChat = async () => {
    if (selectedUsers.length < 2 || !groupChatName.trim()) return;

    try {
      const participants = [actualSenderId, ...selectedUsers.map(u => u.id)];
      const unreadCounts = {};
      participants.forEach(uid => {
        unreadCounts[uid] = 0;
      });

      const conversationData = {
        participants,
        type: 'group',
        name: groupChatName.trim(),
        lastMessage: null,
        lastActivity: serverTimestamp(),
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        unreadCounts,
        isActive: true
      };

      const docRef = await addDoc(collection(db, 'conversations'), conversationData);
      
      // Add system message about group creation
      await addDoc(collection(db, 'conversations', docRef.id, 'messages'), {
        text: `${user.username || user.firstName || 'Someone'} created the group "${groupChatName}"`,
        senderId: 'system',
        senderName: 'System',
        timestamp: serverTimestamp(),
        type: 'system',
        readBy: {} // System messages don't need read tracking
      });

      const newConversation = {
        id: docRef.id,
        ...conversationData
      };
      
      setSelectedConversation(newConversation);
      setShowNewMessageModal(false);
      setSelectedUsers([]);
      setGroupChatName('');
      setSearchUsers('');
      setFoundUsers([]);
    } catch (error) {
      console.error('Error creating group chat:', error);
    }
  };

  // Handle key press in message input
  const handleKeyPress = (event) => {
    if (event.nativeEvent.key === 'Enter' && !event.nativeEvent.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) return;

    // Re-enable when gating messaging for non-members:
    // if (!user?.isCommunityMember) {
    //   alert('You must be a community member to send messages');
    //   return;
    // }

    try {
      setMessageText('');
      let conversationId = selectedConversation.id;
      
      // If in compose mode, create the conversation first
      if (selectedConversation.composeMode) {
        const targetUserId = selectedConversation.targetUserId;
        
        // Check if conversation already exists (race condition protection)
        const existingConversationQuery = query(
          collection(db, 'conversations'),
          where('participants', '==', [actualSenderId, targetUserId].sort()),
          where('type', '==', 'direct')
        );

        const existingSnapshot = await getDocs(existingConversationQuery);
        
        if (!existingSnapshot.empty) {
          // Conversation already exists, use it
          conversationId = existingSnapshot.docs[0].id;
          const existingConversationData = existingSnapshot.docs[0].data();
          
          // Update selected conversation with real data
          setSelectedConversation({
            id: conversationId,
            ...existingConversationData,
            participantUsers: await fetchParticipantUsers(existingConversationData.participants)
          });
        } else {
          // Create new conversation
          const conversationData = {
            participants: [actualSenderId, targetUserId],
            type: 'direct',
            name: null,
            lastMessage: null,
            lastActivity: serverTimestamp(),
            createdAt: serverTimestamp(),
            createdBy: actualSenderId,
            unreadCounts: {
              [actualSenderId]: 0,
              [targetUserId]: 0
            },
            isActive: true
          };

          const docRef = await addDoc(collection(db, 'conversations'), conversationData);
          conversationId = docRef.id;
          
          // Update selected conversation with real data
          setSelectedConversation({
            id: conversationId,
            ...conversationData,
            participantUsers: await fetchParticipantUsers(conversationData.participants)
          });
        }
      }

      // Get sender name and photo from senderData
      const senderName = senderData?.entityType === 'organization'
        ? (senderData.username || senderData.title || 'Organization')
        : (senderData?.username || senderData?.firstName || user?.username || user?.firstName || 'Anonymous');
      const senderPhoto = senderData?.photoURL || user?.photoURL || '';
      
      const messageData = {
        text: messageText.trim(),
        senderId: actualSenderId,
        senderName: senderName,
        senderPhoto: senderPhoto,
        timestamp: serverTimestamp(),
        type: 'text',
        readBy: { [actualSenderId]: serverTimestamp() }, // Store as object with timestamp
        edited: false
      };

      // Add message to conversation
      await addDoc(collection(db, 'conversations', conversationId, 'messages'), messageData);

      // Update conversation's last message and activity
      await updateDoc(doc(db, 'conversations', conversationId), {

        lastMessage: {
          text: messageData.text,
          senderId: messageData.senderId,
          timestamp: messageData.timestamp,
          type: messageData.type
        },
        lastActivity: serverTimestamp(),
        // Increment unread counts for other participants
        ...selectedConversation.participants
          .filter(uid => uid !== actualSenderId)
          .reduce((acc, uid) => {
            acc[`unreadCounts.${uid}`] = (selectedConversation.unreadCounts?.[uid] || 0) + 1;
            return acc;
          }, {})
      });

      setMessageText('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleTourInviteResponse = async (message, status) => {
    if (!message?.tourInviteId || !actualSenderId || !selectedConversation?.id) return;
    try {
      setTourInviteRespondingId(message.id);
      await respondToTourInvite(message.tourInviteId, status, actualSenderId);
      await updateDoc(doc(db, 'conversations', selectedConversation.id, 'messages', message.id), {
        inviteStatus: status,
      });
    } catch (error) {
      console.error('Tour invite response', error);
    } finally {
      setTourInviteRespondingId('');
    }
  };

  // Helper function to fetch participant user/organization data
  const fetchParticipantUsers = async (participants) => {
    const participantUsers = [];
    for (const participantId of participants) {
      try {
        // First try to fetch as a user
        const userDocRef = doc(db, 'users', participantId);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          participantUsers.push({
            id: participantId,
            entityType: 'user',
            ...userDocSnap.data()
          });
        } else {
          // If not found in users, try organizations collection
          const orgDocRef = doc(db, 'organizations', participantId);
          const orgDocSnap = await getDoc(orgDocRef);
          
          if (orgDocSnap.exists()) {
            participantUsers.push({
              id: participantId,
              entityType: 'organization',
              ...orgDocSnap.data()
            });
          } else {
            // Fallback for entities that don't exist in the database
            participantUsers.push({
              id: participantId,
              entityType: 'user',
              firstName: 'Unknown',
              lastName: 'User',
              username: `user_${participantId.substring(0, 8)}`,
              photoURL: null
            });
          }
        }
      } catch (error) {
        console.error(`Error fetching participant data for ${participantId}:`, error);
        // Fallback user data
        participantUsers.push({
          id: participantId,
          entityType: 'user',
          firstName: 'Unknown',
          lastName: 'User', 
          username: `user_${participantId.substring(0, 8)}`,
          photoURL: null
        });
      }
    }
    return participantUsers;
  };

  // Helper function to check if a user has read a message (handles both array and object formats)
  const hasUserReadMessage = (readBy, userId) => {
    if (!readBy) return false;
    // Handle new format: object with timestamps { userId: timestamp }
    if (typeof readBy === 'object' && !Array.isArray(readBy)) {
      return readBy.hasOwnProperty(userId);
    }
    // Handle old format: array of user IDs
    if (Array.isArray(readBy)) {
      return readBy.includes(userId);
    }
    return false;
  };

  // Helper function to get read timestamp for a user
  const getReadTimestamp = (readBy, userId) => {
    if (!readBy) return null;
    // Handle new format: object with timestamps { userId: timestamp }
    if (typeof readBy === 'object' && !Array.isArray(readBy)) {
      return readBy[userId] || null;
    }
    // Old format doesn't have timestamps
    return null;
  };

  // Mark messages as read
  const markMessagesAsRead = async (messagesToCheck = null) => {
    if (!selectedConversation || !actualSenderId || selectedConversation.composeMode) return;

    try {
      // Use provided messages or fall back to state
      const messagesList = messagesToCheck || messages;
      
      // Get all messages that haven't been read by the current user
      const unreadMessages = messagesList.filter(message => {
        // Don't mark system messages or own messages as read
        if (message.type === 'system' || message.senderId === actualSenderId) {
          return false;
        }
        // Check if message has been read by current user
        return !hasUserReadMessage(message.readBy, actualSenderId);
      });

      // Mark each unread message as read with timestamp
      if (unreadMessages.length > 0) {
        const readTimestamp = serverTimestamp();
        const batch = unreadMessages.map(message => {
          const messageRef = doc(db, 'conversations', selectedConversation.id, 'messages', message.id);
          
          // Get current readBy data
          const currentReadBy = message.readBy || {};
          let updatedReadBy;
          
          // If it's an array (old format), convert to object
          if (Array.isArray(currentReadBy)) {
            updatedReadBy = {};
            currentReadBy.forEach(userId => {
              updatedReadBy[userId] = serverTimestamp(); // Use serverTimestamp for existing reads
            });
            updatedReadBy[actualSenderId] = readTimestamp;
          } else {
            // It's already an object, just add/update the current user
            updatedReadBy = {
              ...currentReadBy,
              [actualSenderId]: readTimestamp
            };
          }
          
          return updateDoc(messageRef, { readBy: updatedReadBy });
        });
        
        // Execute all updates in parallel
        await Promise.all(batch);
      }

      // Reset unread count for current sender
      await updateDoc(doc(db, 'conversations', selectedConversation.id), {
        [`unreadCounts.${actualSenderId}`]: 0
      });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Get conversation display name
  const getConversationDisplayName = (conversation) => {
    if (conversation?.kind === 'tourChat' || conversation?.isTourChat) {
      return conversation.name || conversation.tourName || 'Tour chat';
    }
    if (conversation.isSystemConversation) {
      return 'Team VRC';
    }
    
    if (conversation.type === 'group') {
      return conversation.name || 'Group Chat';
    }
    
    // For compose mode, show the target user's name
    if (conversation.composeMode && targetUserData) {
      // Handle organizations differently
      if (targetUserData.entityType === 'organization') {
        return targetUserData.username || targetUserData.title || `Organization ${targetUserData.id.substring(0, 8)}...`;
      }
      const displayName = targetUserData.firstName && targetUserData.lastName 
        ? `${targetUserData.firstName} ${targetUserData.lastName}`
        : targetUserData.username || `User ${targetUserData.id.substring(0, 8)}...`;
      return displayName;
    }
    
    // For direct messages, show the other person's name
    const otherUser = conversation.participantUsers?.find(participant => participant.id !== actualSenderId);
    
    if (otherUser) {
      // Handle organizations differently
      if (otherUser.entityType === 'organization') {
        return otherUser.username || otherUser.title || `Organization ${otherUser.id.substring(0, 8)}...`;
      }
      // Use firstName + lastName, fallback to username, then fallback to ID
      const displayName = otherUser.firstName && otherUser.lastName 
        ? `${otherUser.firstName} ${otherUser.lastName}`
        : otherUser.username || `User ${otherUser.id.substring(0, 8)}...`;
      return displayName;
    }
    
    // Fallback if no user data is available yet
    const otherUserId = conversation.participants.find(id => id !== actualSenderId);
    return `User ${otherUserId?.substring(0, 8)}...`;
  };

  // Get unread count for conversation
  const getUnreadCount = (conversation) => {
    return conversation.unreadCounts?.[actualSenderId] || 0;
  };

  // Get conversation avatar
  const getConversationAvatar = (conversation) => {
    if (conversation?.kind === 'tourChat' || conversation?.isTourChat) {
      return {
        type: 'initial',
        content: personInitials(conversation.tourName || conversation.name, conversation.tourId),
      };
    }
    if (conversation.isSystemConversation) {
      return { type: 'image', content: require('../../assets/logo.png') };
    }

    if (conversation.type === 'group') {
      return { type: 'emoji', content: '👥' };
    }
    
    // For compose mode, show the target user's avatar
    if (conversation.composeMode && targetUserData) {
      if (targetUserData.photoURL) {
        return { type: 'image', content: targetUserData.photoURL };
      }
      return {
        type: 'initial',
        content: personInitials(
          displayNameFromUserLike(targetUserData) || targetUserData.title,
          targetUserData.email || targetUserData.username
        ),
      };
    }
    
    // For direct messages, show the other person's photo
    const otherUser = conversation.participantUsers?.find(participant => participant.id !== actualSenderId);
    
    if (otherUser?.photoURL) {
      return { type: 'image', content: otherUser.photoURL };
    }
    if (otherUser) {
      return {
        type: 'initial',
        content: personInitials(
          displayNameFromUserLike(otherUser) || otherUser.title,
          otherUser.email || otherUser.username || otherUser.id
        ),
      };
    }
    
    return { type: 'initial', content: '?' };
  };

  const listedConversations = showTourCommunityHub
    ? [...tourChatThreads, ...conversations]
    : conversations;

  const openConversation = (conversation) => {
    if (conversation?.kind === 'tourChat' || conversation?.isTourChat) {
      closeModal?.();
      navigation.navigate('MyCalendar', { canvas: 'chat', tourId: conversation.tourId });
      return;
    }
    setSelectedConversation(conversation);
  };

  const renderConversationAvatar = (conversation, imageStyle, textStyle) => {
    const avatar = getConversationAvatar(conversation);
    if (avatar.type === 'image') {
      const source = avatarImageSource(avatar.content);
      if (source) {
        return <Image source={source} style={imageStyle} />;
      }
    }
    if (conversation?.kind === 'tourChat' || conversation?.isTourChat) {
      return (
        <PersonAvatar
          name={conversation.tourName || conversation.name}
          uid={conversation.tourId}
          size={imageStyle?.width || 50}
          style={imageStyle}
          textStyle={textStyle}
        />
      );
    }
    const otherUser = conversation.participantUsers?.find((participant) => participant.id !== actualSenderId)
      || (conversation.composeMode ? targetUserData : null);
    return (
      <PersonAvatar
        photoURL={otherUser?.photoURL}
        name={displayNameFromUserLike(otherUser) || otherUser?.title || avatar.content}
        email={otherUser?.email}
        uid={otherUser?.id}
        size={imageStyle?.width || 50}
        style={imageStyle}
        textStyle={textStyle}
      />
    );
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString();
    }
  };

  if (!user) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Please log in to access messages</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.contentWrapper}>
      {!selectedConversation ? (
        // Conversations List View
        <View style={styles.conversationsContainer}>
          <View style={styles.messagesHeader}>
            <Text style={styles.messagesTitle}>Messages</Text>
            <TouchableOpacity
              style={styles.newMessageButton}
              onPress={() => {
                setShowNewMessageModal(true);
              }}
            >
              <Text style={styles.newMessageButtonText}>+ New</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.conversationsList}>
            {isLoadingConversations && listedConversations.length === 0 ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#4e7de0" />
                <Text style={styles.loadingText}>Loading conversations...</Text>
              </View>
            ) : listedConversations.length === 0 ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyStateText}>No conversations yet</Text>
                <Text style={styles.emptyStateSubtext}>Start a new conversation to connect with other users</Text>
              </View>
            ) : (
               listedConversations.map((conversation) => {
                 const unreadCount = getUnreadCount(conversation);
                 return (
                   <TouchableOpacity
                     key={conversation.id}
                     style={styles.conversationItem}
                     onPress={() => openConversation(conversation)}
                   >
                     <View style={styles.conversationAvatar}>
                       {renderConversationAvatar(
                         conversation,
                         styles.conversationAvatarImage,
                         styles.conversationAvatarText
                       )}
                     </View>
                    <View style={styles.conversationDetails}>
                      <View style={styles.conversationHeader}>
                        <Text style={styles.conversationName}>
                          {getConversationDisplayName(conversation)}
                        </Text>
                        {conversation.lastMessage && (
                          <Text style={styles.conversationTime}>
                            {formatTimestamp(conversation.lastMessage.timestamp)}
                          </Text>
                        )}
                      </View>
                      {conversation.lastMessage && (
                        <Text style={styles.conversationLastMessage} numberOfLines={1}>
                          {conversation.lastMessage.text}
                        </Text>
                      )}
                    </View>
                    {unreadCount > 0 && (
                      <View style={styles.unreadBadge}>
                        <Text style={styles.unreadBadgeText}>{unreadCount}</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                );
              })
            )}
          </ScrollView>

          {/* New Message Modal */}
          {showNewMessageModal && (
            <View style={styles.modalOverlay}>
              <View style={styles.newMessageModal}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>New Message</Text>
                  <TouchableOpacity
                    style={styles.modalCloseButton}
                    onPress={() => {
                      setShowNewMessageModal(false);
                      setSearchUsers('');
                      setFoundUsers([]);
                      setSelectedUsers([]);
                      setGroupChatName('');
                    }}
                  >
                    <Text style={styles.modalCloseText}>✕</Text>
                  </TouchableOpacity>
                </View>

                <TextInput
                  style={styles.searchInput}
                  placeholder="Search users by username, name, or email..."
                  placeholderTextColor="rgba(255, 255, 255, 0.6)"
                  value={searchUsers}
                  onChangeText={handleUserSearch}
                />

                <ScrollView style={styles.usersList}>
                  {foundUsers.map((foundUser) => (
                    <TouchableOpacity
                      key={foundUser.id}
                      style={styles.userItem}
                      onPress={() => {
                        if (selectedUsers.some(u => u.id === foundUser.id)) {
                          setSelectedUsers(selectedUsers.filter(u => u.id !== foundUser.id));
                        } else {
                          setSelectedUsers([...selectedUsers, foundUser]);
                        }
                      }}
                    >
                      <View style={styles.userAvatar}>
                        <PersonAvatar
                          photoURL={foundUser.photoURL}
                          name={displayNameFromUserLike(foundUser)}
                          email={foundUser.email}
                          uid={foundUser.id}
                          size={40}
                          style={styles.userAvatarImage}
                          textStyle={styles.userAvatarText}
                        />
                      </View>
                                             <View style={styles.userInfo}>
                         <Text style={styles.userName}>
                           {foundUser.firstName && foundUser.lastName 
                             ? `${foundUser.firstName} ${foundUser.lastName}`
                             : foundUser.username || 'Anonymous'
                           }
                         </Text>
                         {foundUser.username && foundUser.firstName && foundUser.lastName && (
                           <Text style={styles.userUsername}>
                             @{foundUser.username}
                           </Text>
                         )}
                       </View>
                      {selectedUsers.some(u => u.id === foundUser.id) && (
                        <Text style={styles.selectedIndicator}>✓</Text>
                      )}
                    </TouchableOpacity>
                  ))}
                </ScrollView>

                {selectedUsers.length > 1 && (
                  <TextInput
                    style={styles.groupNameInput}
                    placeholder="Group chat name..."
                    placeholderTextColor="rgba(255, 255, 255, 0.6)"
                    value={groupChatName}
                    onChangeText={setGroupChatName}
                  />
                )}

                <View style={styles.modalActions}>
                  {selectedUsers.length === 1 && (
                    <TouchableOpacity
                      style={styles.startChatButton}
                      onPress={() => {
                        startConversation(selectedUsers[0]);
                      }}
                    >
                      <Text style={styles.startChatButtonText}>Start Chat</Text>
                    </TouchableOpacity>
                  )}
                  {selectedUsers.length > 1 && (
                    <TouchableOpacity
                      style={[styles.startChatButton, !groupChatName.trim() && styles.disabledButton]}
                      onPress={() => {
                        createGroupChat();
                      }}
                      disabled={!groupChatName.trim()}
                    >
                      <Text style={styles.startChatButtonText}>Create Group</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            </View>
          )}
        </View>
      ) : (
        // Chat View
          <View style={styles.chatContainer}>
           <View style={styles.chatHeader}>
             <TouchableOpacity
               style={styles.backButton}
               onPress={() => setSelectedConversation(null)}
             >
               <Text style={styles.backButtonText}>← Back</Text>
             </TouchableOpacity>
             
             <View style={styles.chatHeaderInfo}>
               <View style={styles.chatHeaderAvatar}>
                 {renderConversationAvatar(
                   selectedConversation,
                   styles.chatHeaderAvatarImage,
                   styles.chatHeaderAvatarText
                 )}
               </View>
               <Text style={styles.chatTitle}>
                 {getConversationDisplayName(selectedConversation)}
               </Text>
             </View>
           </View>

                     <ScrollView
             ref={messagesScrollRef}
             style={styles.messagesContainer}
             contentContainerStyle={styles.messagesContent}
           >
             {selectedConversation.composeMode ? (
               <View style={styles.composePlaceholder}>
                 <Text style={styles.composePlaceholderText}>
                   {targetUserData ? 
                     `Start a conversation with ${targetUserData.entityType === 'organization' 
                       ? (targetUserData.username || targetUserData.title || 'this organization')
                       : (targetUserData.firstName && targetUserData.lastName ? 
                           `${targetUserData.firstName} ${targetUserData.lastName}` : 
                           targetUserData.username || 'this user')} by typing a message below` :
                     'Start a conversation by typing a message below'
                   }
                 </Text>
               </View>
             ) : (
               messages.map((message, index) => {
                 const isOwnMessage = message.senderId === actualSenderId;
                 const isSystemMessage = message.type === 'system';
                 const isTourInviteMessage = message.type === 'tour_invite';
                 const isCalendarShareMessage = message.type === 'calendar_share';
                 const isSetlistProposalMessage = message.type === 'setlist_proposal';
                 const tourInviteStatus = String(message.inviteStatus || 'pending').toLowerCase();
                 const isLatestMessage = index === messages.length - 1;
                 
                // For own messages, check if recipient has read it
                // For other messages, check if we've read it
                const readBy = message.readBy || {};
                let isRead = false;
                let readReceiptInfo = null;
                
                if (isOwnMessage && !isSystemMessage) {
                  // For own messages, check if any other participant has read it
                  const otherParticipants = selectedConversation.participants?.filter(id => id !== actualSenderId) || [];
                  isRead = otherParticipants.some(participantId => hasUserReadMessage(readBy, participantId));
                  
                  // Get read receipt info for direct messages
                  if (selectedConversation.type === 'direct' && otherParticipants.length === 1) {
                    const recipientId = otherParticipants[0];
                    isRead = hasUserReadMessage(readBy, recipientId);
                    if (isRead) {
                      // Find recipient data for display
                      const recipient = selectedConversation.participantUsers?.find(p => p.id === recipientId);
                      if (recipient) {
                        let recipientName;
                        // For organizations, use username or title
                        if (recipient.entityType === 'organization') {
                          recipientName = recipient.username || recipient.title || 'Organization';
                        } else {
                          // For users, use firstName or fallback to username
                          recipientName = recipient.firstName || recipient.username || 'User';
                        }
                        readReceiptInfo = {
                          readNames: [recipientName]
                        };
                      }
                    }
                  } else if (selectedConversation.type === 'group') {
                    // For group messages, show who has read it
                    const readParticipants = otherParticipants.filter(id => hasUserReadMessage(readBy, id));
                    const readCount = readParticipants.length;
                    const totalParticipants = otherParticipants.length;
                    isRead = readCount > 0;
                    if (isRead) {
                      // Get first names of people who have read the message
                      const readNames = readParticipants
                        .map(id => {
                          const participant = selectedConversation.participantUsers?.find(p => p.id === id);
                          if (participant) {
                            // For organizations, use username or title
                            if (participant.entityType === 'organization') {
                              return participant.username || participant.title || 'Organization';
                            }
                            // For users, use firstName or fallback to username
                            return participant.firstName || participant.username || 'User';
                          }
                          return null;
                        })
                        .filter(name => name !== null);
                      
                      readReceiptInfo = {
                        readCount,
                        totalParticipants,
                        readNames: readNames
                      };
                    }
                  }
                } else if (!isOwnMessage && !isSystemMessage) {
                  // For received messages, check if we've read it
                  isRead = hasUserReadMessage(readBy, actualSenderId);
                  
                  // Get read receipt info for other users' messages
                  const allParticipants = selectedConversation.participants || [];
                  const otherParticipants = allParticipants.filter(id => id !== message.senderId);
                  
                  // Get list of people who have read this message (excluding sender)
                  const readParticipants = otherParticipants.filter(id => hasUserReadMessage(readBy, id));
                  
                  if (readParticipants.length > 0) {
                    // Get first names of people who have read the message
                    const readNames = readParticipants
                      .map(id => {
                        const participant = selectedConversation.participantUsers?.find(p => p.id === id);
                        if (participant) {
                          // For organizations, use username or title
                          if (participant.entityType === 'organization') {
                            return participant.username || participant.title || 'Organization';
                          }
                          // For users, use firstName or fallback to username
                          return participant.firstName || participant.username || 'User';
                        }
                        return null;
                      })
                      .filter(name => name !== null);
                    
                    readReceiptInfo = {
                      readCount: readNames.length,
                      totalParticipants: otherParticipants.length,
                      readNames: readNames
                    };
                  }
                }
                 
                 const isExpanded = expandedMessageId === message.id;
                 // Allow expanding any message (own or other) that has read receipt info and is not the latest
                 const canExpand = !isSystemMessage && !isLatestMessage && readReceiptInfo && 
                   ((isOwnMessage && isRead) || (!isOwnMessage && readReceiptInfo.readNames && readReceiptInfo.readNames.length > 0));
                 
                 return (
                   <TouchableOpacity
                     key={message.id}
                     style={[
                       styles.messageContainer,
                       isOwnMessage && styles.ownMessageContainer,
                       canExpand && styles.messageClickable
                     ]}
                     onPress={() => {
                       // Toggle expanded state for older messages with read receipts
                       if (canExpand) {
                         setExpandedMessageId(isExpanded ? null : message.id);
                       }
                     }}
                     activeOpacity={canExpand ? 0.8 : 1}
                   >

                       <>
                         {!isOwnMessage && !isSystemMessage && (
                           <Text style={styles.messageSender}>{message.senderName}</Text>
                         )}

                         <View style={[
                           styles.messageBubble,
                           isOwnMessage ? styles.ownMessageBubble : styles.otherMessageBubble
                         ]}>
                           {message.type === 'gear_swap_interest' && message.listingPreview && (
                             <View style={[
                               styles.gearSwapPreviewCard,
                               { borderColor: GEAR_SWAP_TYPE_COLORS[message.listingPreview.listingType] || '#424251' }
                             ]}>
                               <View style={styles.gearSwapPreviewHeader}>
                                 <Text style={styles.gearSwapPreviewName} numberOfLines={1}>
                                   {message.listingPreview.displayName || 'Listing'}
                                 </Text>
                                 {message.listingPreview.listingType ? (
                                   <View style={[styles.gearSwapPreviewBadge, { backgroundColor: GEAR_SWAP_TYPE_COLORS[message.listingPreview.listingType] || '#424251' }]}>
                                     <Text style={styles.gearSwapPreviewBadgeText}>{message.listingPreview.listingType}</Text>
                                   </View>
                                 ) : null}
                               </View>
                               {message.listingPreview.location ? (
                                 <Text style={styles.gearSwapPreviewLocation} numberOfLines={1}>{message.listingPreview.location}</Text>
                               ) : null}
                               {message.listingPreview.price != null && message.listingPreview.price !== '' ? (
                                 <Text style={styles.gearSwapPreviewPrice}>${message.listingPreview.price}</Text>
                               ) : null}
                               {Array.isArray(message.listingPreview.items) && message.listingPreview.items.length > 0 ? (
                                 <View style={styles.gearSwapPreviewGridWrap}>
                                   <PostGridDisplay
                                     items={message.listingPreview.items}
                                     colWidth={42}
                                     rowHeight={28}
                                     style={styles.gearSwapPreviewGrid}
                                     suppressEmptyWidgetHints
                                   />
                                 </View>
                               ) : (
                                 <>
                                   {message.listingPreview.firstText ? (
                                     <Text style={styles.gearSwapPreviewSnippet} numberOfLines={2}>{message.listingPreview.firstText}</Text>
                                   ) : null}
                                   {message.listingPreview.firstImageUrl ? (
                                     <Image source={{ uri: message.listingPreview.firstImageUrl }} style={styles.gearSwapPreviewThumb} resizeMode="cover" />
                                   ) : null}
                                 </>
                               )}
                             </View>
                           )}
                           {isTourInviteMessage ? (
                             <View style={styles.tourInviteCard}>
                               <Text style={styles.tourInviteTitle}>
                                 Tour invite · {message.tourName || 'Tour'}
                               </Text>
                               <Text style={[
                                 styles.messageText,
                                 isOwnMessage ? styles.ownMessageText : styles.otherMessageText,
                               ]}>
                                 {message.text}
                               </Text>
                               {tourInviteStatus === 'accepted' ? (
                                 <Text style={styles.tourInviteStatusAccepted}>Accepted</Text>
                               ) : tourInviteStatus === 'declined' ? (
                                 <Text style={styles.tourInviteStatusDeclined}>Declined</Text>
                               ) : !isOwnMessage ? (
                                 <View style={styles.tourInviteActions}>
                                   <TouchableOpacity
                                     style={[styles.tourInviteBtn, styles.tourInviteBtnAccept]}
                                     disabled={tourInviteRespondingId === message.id}
                                     onPress={() => handleTourInviteResponse(message, 'accepted')}
                                   >
                                     <Text style={styles.tourInviteBtnText}>
                                       {tourInviteRespondingId === message.id ? '…' : 'Accept'}
                                     </Text>
                                   </TouchableOpacity>
                                   <TouchableOpacity
                                     style={[styles.tourInviteBtn, styles.tourInviteBtnDecline]}
                                     disabled={tourInviteRespondingId === message.id}
                                     onPress={() => handleTourInviteResponse(message, 'declined')}
                                   >
                                     <Text style={styles.tourInviteBtnText}>
                                       {tourInviteRespondingId === message.id ? '…' : 'Decline'}
                                     </Text>
                                   </TouchableOpacity>
                                 </View>
                               ) : (
                                 <Text style={styles.tourInviteStatusPending}>Awaiting response</Text>
                               )}
                             </View>
                           ) : isCalendarShareMessage || isSetlistProposalMessage ? (
                             <View style={styles.tourInviteCard}>
                               <Text style={styles.tourInviteTitle}>
                                 {isSetlistProposalMessage ? 'Setlist proposal' : 'Shared day'} · {message.tourName || 'Tour'}
                                 {message.dateLabel ? ` · ${message.dateLabel}` : ''}
                               </Text>
                               <Text style={[
                                 styles.messageText,
                                 isOwnMessage ? styles.ownMessageText : styles.otherMessageText,
                               ]}>
                                 {message.text}
                               </Text>
                               {message.shareUrl || message.calendarPath ? (
                                 <TouchableOpacity
                                   style={[styles.tourInviteBtn, styles.tourInviteBtnAccept, { alignSelf: 'flex-start' }]}
                                   onPress={() => {
                                     const url = String(message.shareUrl || '').trim()
                                       || (typeof window !== 'undefined' ? `${window.location.origin}${message.calendarPath}` : message.calendarPath);
                                     if (!url) return;
                                     if (Platform.OS === 'web') {
                                       window.location.assign(url);
                                     } else {
                                       Linking.openURL(url).catch(() => {});
                                     }
                                   }}
                                 >
                                   <Text style={styles.tourInviteBtnText}>
                                     {isSetlistProposalMessage ? 'Open setlist' : 'View shared day'}
                                   </Text>
                                 </TouchableOpacity>
                               ) : null}
                             </View>
                           ) : (
                             <Text style={[
                               styles.messageText,
                               isOwnMessage ? styles.ownMessageText : styles.otherMessageText
                             ]}>
                               {message.text}
                             </Text>
                           )}
                         </View>
                         {/* Only show timestamp for latest message */}
                         {isLatestMessage && (
                           <View style={styles.messageFooter}>
                             <Text style={styles.messageTime}>
                               {formatTimestamp(message.timestamp)}
                             </Text>
                             {isOwnMessage && !isSystemMessage && (
                               <View style={styles.readReceiptContainer}>
                                 {/* Only show checkmark for unread messages - older read messages show nothing here */}
                                 {!isRead && (
                                   <Ionicons 
                                     name="checkmark" 
                                     size={16} 
                                     color="rgba(255, 255, 255, 0.5)" 
                                     style={styles.readReceiptIcon}
                                   />
                                 )}
                               </View>
                             )}
                           </View>
                         )}
                         {/* Show read receipt info below message when expanded or if it's the latest message */}
                         {!isSystemMessage && readReceiptInfo && (isLatestMessage || isExpanded) && readReceiptInfo.readNames && readReceiptInfo.readNames.length > 0 && (
                           <View style={[
                             styles.readReceiptInfoContainer,
                             !isOwnMessage && styles.readReceiptInfoContainerLeft
                           ]}>
                             <Ionicons 
                               name="checkmark-done" 
                               size={16} 
                               color="#4e7de0" 
                               style={styles.readReceiptIcon}
                             />
                             <Text style={styles.readReceiptText}>
                               Read by {readReceiptInfo.readNames.join(', ')}
                             </Text>
                             {/* Show timestamp next to read receipt */}
                             <Text style={styles.readReceiptTimestamp}>
                               {formatTimestamp(message.timestamp)}
                             </Text>
                           </View>
                         )}
                       </>
                   </TouchableOpacity>
                 );
               })
             )}
           </ScrollView>

          {/* Check if this is a Team VRC system conversation - disable replies */}
          {selectedConversation?.isSystemConversation || 
           (selectedConversation?.participants?.includes('team_vrc') && 
            selectedConversation?.participants?.length === 2) ? (
            <View style={[styles.messageInputContainer, { opacity: 0.5 }]}>
              <TextInput
                style={[styles.messageInput, { backgroundColor: 'rgba(255, 255, 255, 0.1)' }]}
                placeholder="This is a system notification. Replies are not allowed."
                placeholderTextColor="rgba(255, 255, 255, 0.4)"
                value=""
                editable={false}
                multiline
                maxLength={500}
              />
              <TouchableOpacity
                style={[styles.sendButton, styles.disabledButton]}
                disabled={true}
              >
                <Text style={styles.sendButtonText}>Send</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.messageInputContainer}>
              <TextInput
                style={styles.messageInput}
                placeholder="Type a message..."
                placeholderTextColor="rgba(255, 255, 255, 0.6)"
                value={messageText}
                onChangeText={setMessageText}
                onKeyPress={handleKeyPress}
                multiline
                maxLength={500}
              />
              <TouchableOpacity
                style={[styles.sendButton, !messageText.trim() && styles.disabledButton]}
                onPress={sendMessage}
                disabled={!messageText.trim()}
              >
                <Text style={styles.sendButtonText}>Send</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}
      </View>
      
      {/* Overlay for non-community members — disabled; restore if gating messaging again.
      {!isCommunityMember && (
        <View style={styles.overlayContainer}>
          <CommunityMemberUpgradeForm
            message="You must be a community member to access messaging features."
            onSuccess={() => {
              setShowCommunityMemberSuccessModal(true);
            }}
          />
        </View>
      )}
      */}

      {/* Community Member Success Modal */}
      <FullscreenSafeModal
        visible={showCommunityMemberSuccessModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowCommunityMemberSuccessModal(false)}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}>
          <View style={{
            backgroundColor: '#ffffff',
            borderRadius: 12,
            padding: 20,
            width: '100%',
            maxWidth: 400,
            alignItems: 'center',
          }}>
            <Ionicons name="checkmark-circle" size={48} color="#2ecc71" />
            <Text style={{
              fontSize: 18,
              fontFamily: 'Glacial Indifference',
              marginTop: 10,
              marginBottom: 15,
              textAlign: 'center',
              color: '#2ecc71',
            }}>Congratulations!</Text>

            <Text style={{
              fontSize: 14,
              color: '#333',
              textAlign: 'center',
              marginBottom: 20,
              lineHeight: 20,
            }}>You are now a community member.</Text>

            <TouchableOpacity
              onPress={() => setShowCommunityMemberSuccessModal(false)}
              style={{
                backgroundColor: '#2ecc71',
                borderRadius: 8,
                paddingHorizontal: 30,
                paddingVertical: 12,
                alignItems: 'center',
              }}
            >
              <Text style={{
                color: '#ffffff',
                fontSize: 16,
                fontFamily: 'Glacial Indifference',
                fontWeight: 'bold',
              }}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </FullscreenSafeModal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    backgroundColor: 'transparent',
  },
  conversationsContainer: {
    flex: 1,
    height: '100%'
  },
  messagesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  messagesTitle: {
    color: '#ffffff',
    fontSize: 20,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  newMessageButton: {
    backgroundColor: '#4e7de0',
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 20,
  },
  newMessageButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  conversationsList: {
    flex: 1,
    height: '100%'
  },
  conversationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.05)',
  },
  conversationAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 15,
  },
     conversationAvatarText: {
     fontSize: 20,
   },
   conversationAvatarImage: {
     width: 50,
     height: 50,
     borderRadius: 25,
   },
  conversationDetails: {
    flex: 1,
  },
  conversationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
  },
  conversationName: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  conversationTime: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
  },
  conversationLastMessage: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
  },
  unreadBadge: {
    backgroundColor: '#4e7de0',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  unreadBadgeText: {
    color: '#ffffff',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    minHeight: 200,
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    marginTop: 15,
    textAlign: 'center',
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  emptyStateText: {
    color: '#ffffff',
    fontSize: 18,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: 10,
  },
  emptyStateSubtext: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
  },
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  newMessageModal: {
    backgroundColor: '#1a1a1a',
    borderRadius: 12,
    width: '100%',
    maxWidth: 400,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  modalTitle: {
    color: '#ffffff',
    fontSize: 18,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  modalCloseButton: {
    padding: 5,
  },
  modalCloseText: {
    color: '#ffffff',
    fontSize: 20,
  },
  searchInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    paddingHorizontal: 15,
    paddingVertical: 12,
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    margin: 15,
  },
  usersList: {
    maxHeight: 200,
  },
  userItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.05)',
  },
  userAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 15,
  },
  userAvatarImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  userAvatarText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
     userInfo: {
     flex: 1,
   },
   userName: {
     color: '#ffffff',
     fontSize: 16,
     fontFamily: 'VastagoGrotesk',
     fontWeight: '600',
   },
   userUsername: {
     color: 'rgba(255, 255, 255, 0.6)',
     fontSize: 14,
     fontFamily: 'VastagoGrotesk',
     marginTop: 2,
   },
  selectedIndicator: {
    color: '#4e7de0',
    fontSize: 18,
    fontWeight: 'bold',
  },
  groupNameInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    paddingHorizontal: 15,
    paddingVertical: 12,
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    margin: 15,
    marginTop: 0,
  },
  modalActions: {
    padding: 15,
  },
  startChatButton: {
    backgroundColor: '#4e7de0',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  startChatButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  disabledButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  chatContainer: {
    flex: 1,
    height: '100%',
  },
     chatHeader: {
     flexDirection: 'row',
     alignItems: 'center',
     padding: 15,
     borderBottomWidth: 1,
     borderBottomColor: 'rgba(255, 255, 255, 0.1)',
   },
   backButton: {
     marginRight: 15,
   },
   backButtonText: {
     color: '#4e7de0',
     fontSize: 16,
     fontFamily: 'VastagoGrotesk',
   },
   chatHeaderInfo: {
     flexDirection: 'row',
     alignItems: 'center',
     flex: 1,
   },
   chatHeaderAvatar: {
     width: 35,
     height: 35,
     borderRadius: 17.5,
     backgroundColor: 'rgba(255, 255, 255, 0.1)',
     alignItems: 'center',
     justifyContent: 'center',
     marginRight: 12,
   },
   chatHeaderAvatarImage: {
     width: 35,
     height: 35,
     borderRadius: 17.5,
   },
   chatHeaderAvatarText: {
     fontSize: 16,
     color: '#ffffff',
     fontFamily: 'VastagoGrotesk',
     fontWeight: 'bold',
   },
   chatTitle: {
     color: '#ffffff',
     fontSize: 18,
     fontFamily: 'VastagoGrotesk',
     fontWeight: 'bold',
   },
  messagesContainer: {
    height: '100%',
    flex: 1,
  },
  messagesContent: {
    padding: 15,
    flex: 1,
    width: '100%',
  },
  messageContainer: {
    marginBottom: 5,
  },
  ownMessageContainer: {
    alignItems: 'flex-end',
  },
  systemMessage: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
    fontStyle: 'italic',
    textAlign: 'center',
  },
  messageSender: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
    marginBottom: 5,
  },
  messageBubble: {
    maxWidth: '80%',
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderRadius: 18,
  },
  ownMessageBubble: {
    backgroundColor: '#4e7de0',
  },
  otherMessageBubble: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  messageText: {
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
  },
  ownMessageText: {
    color: '#ffffff',
  },
  otherMessageText: {
    color: '#ffffff',
  },
  gearSwapPreviewCard: {
    borderWidth: 2,
    borderRadius: 10,
    padding: 10,
    marginBottom: 10,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  tourInviteCard: {
    gap: 8,
  },
  tourInviteTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 13,
    fontWeight: '800',
    color: '#97ff11',
  },
  tourInviteActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
  },
  tourInviteBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tourInviteBtnAccept: {
    backgroundColor: '#2ecc71',
  },
  tourInviteBtnDecline: {
    backgroundColor: '#e74c3c',
  },
  tourInviteBtnText: {
    color: '#fff',
    fontWeight: '800',
    fontSize: 13,
  },
  tourInviteStatusPending: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.65)',
    fontStyle: 'italic',
  },
  tourInviteStatusAccepted: {
    fontSize: 12,
    color: '#2ecc71',
    fontWeight: '700',
  },
  tourInviteStatusDeclined: {
    fontSize: 12,
    color: '#e74c3c',
    fontWeight: '700',
  },
  gearSwapPreviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 4,
  },
  gearSwapPreviewName: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 13,
    fontWeight: '600',
    color: '#fff',
    flex: 1,
  },
  gearSwapPreviewBadge: {
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  gearSwapPreviewBadgeText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 10,
    fontWeight: '600',
    color: '#fff',
  },
  gearSwapPreviewLocation: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    color: 'rgba(255,255,255,0.85)',
    marginBottom: 2,
  },
  gearSwapPreviewPrice: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    fontWeight: '700',
    color: '#fff',
    marginBottom: 6,
  },
  gearSwapPreviewGridWrap: {
    marginBottom: 6,
    overflow: 'hidden',
    borderRadius: 6,
  },
  gearSwapPreviewGrid: {
    alignSelf: 'center',
  },
  gearSwapPreviewSnippet: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    color: 'rgba(255,255,255,0.9)',
    marginBottom: 4,
  },
  gearSwapPreviewThumb: {
    width: '100%',
    height: 80,
    borderRadius: 6,
    marginTop: 4,
  },
  messageFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
    gap: 6,
  },
  messageTime: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 11,
    fontFamily: 'VastagoGrotesk',
  },
  readReceiptContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  readReceiptIcon: {
    marginLeft: 2,
  },
  readReceiptText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 10,
    fontFamily: 'VastagoGrotesk',
    marginLeft: 2,
  },
  readReceiptInfoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
    marginLeft: 'auto',
    gap: 6,
  },
  readReceiptInfoContainerLeft: {
    marginLeft: 0,
    marginRight: 'auto',
  },
  readReceiptTimestamp: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 11,
    fontFamily: 'VastagoGrotesk',
    marginLeft: 4,
  },
  messageClickable: {
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  messageInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
  },
  messageInput: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 10,
    paddingHorizontal: 15,
    paddingVertical: 10,
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    maxHeight: 100,
    marginRight: 10,
  },
  sendButton: {
    backgroundColor: '#4e7de0',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
  },
  sendButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
     errorText: {
     color: 'rgba(255, 255, 255, 0.7)',
     fontSize: 16,
     fontFamily: 'VastagoGrotesk',
     textAlign: 'center',
     padding: 40,
   },
   contentWrapper: {
     flex: 1,
     height: '100%',
   },
   contentWrapperDisabled: {
     opacity: 0.5,
   },
  overlayContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: 20,
  },
   overlayMessage: {
     backgroundColor: 'rgba(255, 233, 156, 0.2)',
     padding: 20,
     borderRadius: 12,
     maxWidth: '80%',
     alignItems: 'center',
   },
   overlayText: {
     color: '#E9E9E9',
     fontSize: 16,
     fontFamily: 'VastagoGrotesk',
     fontWeight: '400',
     textAlign: 'center',
   },
   composePlaceholder: {
     flex: 1,
     alignItems: 'center',
     justifyContent: 'center',
     padding: 40,
     minHeight: 200,
   },
   composePlaceholderText: {
     color: 'rgba(255, 255, 255, 0.6)',
     fontSize: 16,
     fontFamily: 'VastagoGrotesk',
     textAlign: 'center',
     fontStyle: 'italic',
   },
 });

export default MessagingComponent;
